import React from 'react';
import Card from '../../ui/Card';
import { CheckCircleIcon, XCircleIcon, ClockIcon, AlertTriangleIcon } from 'lucide-react';
const LaunchChecklistTable: React.FC = () => {
  const products = [{
    name: 'LED Headlamp Pro',
    launchDate: 'Oct 2024',
    items: [{
      category: 'Product Development',
      task: 'Design Finalization',
      status: 'completed',
      owner: 'R&D Team',
      dueDate: 'Jan 2024'
    }, {
      category: 'Product Development',
      task: 'Prototype Testing',
      status: 'completed',
      owner: 'Quality Team',
      dueDate: 'Mar 2024'
    }, {
      category: 'Product Development',
      task: 'Production Tooling',
      status: 'completed',
      owner: 'Operations',
      dueDate: 'May 2024'
    }, {
      category: 'Marketing',
      task: 'GTM Strategy',
      status: 'completed',
      owner: 'Marketing',
      dueDate: 'Jun 2024'
    }, {
      category: 'Marketing',
      task: 'Campaign Materials',
      status: 'in-progress',
      owner: 'Marketing',
      dueDate: 'Sep 2024'
    }, {
      category: 'Sales',
      task: 'Pricing Approval',
      status: 'completed',
      owner: 'Sales',
      dueDate: 'Jul 2024'
    }, {
      category: 'Sales',
      task: 'Distributor Training',
      status: 'in-progress',
      owner: 'Sales',
      dueDate: 'Oct 2024'
    }, {
      category: 'Operations',
      task: 'Supply Chain Setup',
      status: 'completed',
      owner: 'Supply Chain',
      dueDate: 'Aug 2024'
    }, {
      category: 'Operations',
      task: 'Quality Certification',
      status: 'completed',
      owner: 'Quality',
      dueDate: 'Aug 2024'
    }, {
      category: 'Compliance',
      task: 'Regulatory Approvals',
      status: 'completed',
      owner: 'Legal',
      dueDate: 'Jul 2024'
    }]
  }, {
    name: 'Smart Mirror System',
    launchDate: 'Jan 2025',
    items: [{
      category: 'Product Development',
      task: 'Design Finalization',
      status: 'completed',
      owner: 'R&D Team',
      dueDate: 'Feb 2024'
    }, {
      category: 'Product Development',
      task: 'Prototype Testing',
      status: 'completed',
      owner: 'Quality Team',
      dueDate: 'May 2024'
    }, {
      category: 'Product Development',
      task: 'Production Tooling',
      status: 'in-progress',
      owner: 'Operations',
      dueDate: 'Oct 2024'
    }, {
      category: 'Marketing',
      task: 'GTM Strategy',
      status: 'in-progress',
      owner: 'Marketing',
      dueDate: 'Nov 2024'
    }, {
      category: 'Marketing',
      task: 'Campaign Materials',
      status: 'pending',
      owner: 'Marketing',
      dueDate: 'Dec 2024'
    }, {
      category: 'Sales',
      task: 'Pricing Approval',
      status: 'in-progress',
      owner: 'Sales',
      dueDate: 'Oct 2024'
    }, {
      category: 'Sales',
      task: 'Distributor Training',
      status: 'pending',
      owner: 'Sales',
      dueDate: 'Dec 2024'
    }, {
      category: 'Operations',
      task: 'Supply Chain Setup',
      status: 'in-progress',
      owner: 'Supply Chain',
      dueDate: 'Nov 2024'
    }, {
      category: 'Operations',
      task: 'Quality Certification',
      status: 'in-progress',
      owner: 'Quality',
      dueDate: 'Nov 2024'
    }, {
      category: 'Compliance',
      task: 'Regulatory Approvals',
      status: 'at-risk',
      owner: 'Legal',
      dueDate: 'Oct 2024'
    }]
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon size={16} className="text-green-600" data-id="element-7574" />;
      case 'in-progress':
        return <ClockIcon size={16} className="text-blue-600" data-id="element-7575" />;
      case 'at-risk':
        return <AlertTriangleIcon size={16} className="text-red-600" data-id="element-7576" />;
      case 'pending':
        return <XCircleIcon size={16} className="text-gray-400" data-id="element-7577" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-300';
      case 'in-progress':
        return 'text-blue-600 bg-blue-50 border-blue-300';
      case 'at-risk':
        return 'text-red-600 bg-red-50 border-red-300';
      case 'pending':
        return 'text-gray-600 bg-gray-50 border-gray-300';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-300';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Product Development':
        return 'bg-blue-100 text-blue-800';
      case 'Marketing':
        return 'bg-green-100 text-green-800';
      case 'Sales':
        return 'bg-yellow-100 text-yellow-800';
      case 'Operations':
        return 'bg-purple-100 text-purple-800';
      case 'Compliance':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const calculateCompletion = (items: any[]) => {
    const completed = items.filter(item => item.status === 'completed').length;
    return Math.round(completed / items.length * 100);
  };
  return <div className="space-y-8" data-id="element-7578">
      {products.map((product, productIndex) => {
      const completionRate = calculateCompletion(product.items);
      const completedCount = product.items.filter(item => item.status === 'completed').length;
      const inProgressCount = product.items.filter(item => item.status === 'in-progress').length;
      const atRiskCount = product.items.filter(item => item.status === 'at-risk').length;
      const pendingCount = product.items.filter(item => item.status === 'pending').length;
      return <Card key={productIndex} className="p-6" data-id="element-7579">
            <div className="flex items-center justify-between mb-6" data-id="element-7580">
              <div data-id="element-7581">
                <h3 className="text-lg font-semibold text-gray-800" data-id="element-7582">
                  {product.name} - Launch Checklist
                </h3>
                <p className="text-sm text-gray-600" data-id="element-7583">
                  Target Launch: {product.launchDate}
                </p>
              </div>
              <div className="text-right" data-id="element-7584">
                <div className="text-3xl font-bold text-green-600" data-id="element-7585">
                  {completionRate}%
                </div>
                <div className="text-xs text-gray-600" data-id="element-7586">Complete</div>
              </div>
            </div>
            {/* Progress Bar */}
            <div className="mb-6" data-id="element-7587">
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden" data-id="element-7588">
                <div className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500" style={{
              width: `${completionRate}%`
            }} data-id="element-7589" />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-600" data-id="element-7590">
                <span data-id="element-7591">
                  {completedCount} of {product.items.length} tasks completed
                </span>
                <span data-id="element-7592">{product.items.length - completedCount} remaining</span>
              </div>
            </div>
            {/* Status Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6" data-id="element-7593">
              <div className="flex items-center p-3 bg-green-50 rounded-lg" data-id="element-7594">
                <CheckCircleIcon size={20} className="text-green-600 mr-2" data-id="element-7595" />
                <div data-id="element-7596">
                  <div className="text-lg font-bold text-green-600" data-id="element-7597">
                    {completedCount}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-7598">Completed</div>
                </div>
              </div>
              <div className="flex items-center p-3 bg-blue-50 rounded-lg" data-id="element-7599">
                <ClockIcon size={20} className="text-blue-600 mr-2" data-id="element-7600" />
                <div data-id="element-7601">
                  <div className="text-lg font-bold text-blue-600" data-id="element-7602">
                    {inProgressCount}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-7603">In Progress</div>
                </div>
              </div>
              <div className="flex items-center p-3 bg-red-50 rounded-lg" data-id="element-7604">
                <AlertTriangleIcon size={20} className="text-red-600 mr-2" data-id="element-7605" />
                <div data-id="element-7606">
                  <div className="text-lg font-bold text-red-600" data-id="element-7607">
                    {atRiskCount}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-7608">At Risk</div>
                </div>
              </div>
              <div className="flex items-center p-3 bg-gray-50 rounded-lg" data-id="element-7609">
                <XCircleIcon size={20} className="text-gray-400 mr-2" data-id="element-7610" />
                <div data-id="element-7611">
                  <div className="text-lg font-bold text-gray-600" data-id="element-7612">
                    {pendingCount}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-7613">Pending</div>
                </div>
              </div>
            </div>
            {/* Checklist Table */}
            <div className="overflow-x-auto" data-id="element-7614">
              <table className="w-full" data-id="element-7615">
                <thead data-id="element-7616">
                  <tr className="border-b-2 border-gray-300" data-id="element-7617">
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-7618">
                      Status
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-7619">
                      Category
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-7620">
                      Task
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-7621">
                      Owner
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-7622">
                      Due Date
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-7623">
                  {product.items.map((item, itemIndex) => <tr key={itemIndex} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-7624">
                      <td className="py-3 px-4 text-center" data-id="element-7625">
                        <div className="flex items-center justify-center" data-id="element-7626">
                          {getStatusIcon(item.status)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center" data-id="element-7627">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`} data-id="element-7628">
                          {item.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-7629">
                        {item.task}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700" data-id="element-7630">
                        {item.owner}
                      </td>
                      <td className="py-3 px-4 text-center" data-id="element-7631">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(item.status)}`} data-id="element-7632">
                          {item.dueDate}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </Card>;
    })}
    </div>;
};
export default LaunchChecklistTable;